import { Link } from "react-router-dom";
import { 
  ArrowLeft, 
  ArrowRight,
  DollarSign, 
  Coins, 
  Wallet,
  CreditCard,
  Clock,
  CheckCircle,
  ArrowDownCircle,
  Calculator,
  Info,
  AlertCircle,
  Gift
} from "lucide-react";
import { Button } from "../components/ui/button";
import Footer from "../components/Footer";

export default function PointsPaymentsPage() {
  const conversionRates = [
    { points: 100, usd: 1 },
    { points: 500, usd: 5 },
    { points: 700, usd: 7 },
    { points: 1000, usd: 10 },
    { points: 5000, usd: 50 },
    { points: 10000, usd: 100 }
  ];

  const paymentMethods = [
    {
      name: "PayPal",
      icon: "💰",
      processingTime: "1-3 business days",
      minimumWithdrawal: "$7.00",
      fees: "No fees",
      availability: "Worldwide",
      recommended: true
    },
    {
      name: "Custom Payment",
      icon: "🏦",
      processingTime: "3-7 business days",
      minimumWithdrawal: "$25.00",
      fees: "Varies",
      availability: "Contact admin",
      recommended: false
    }
  ];

  const pointsEarning = [
    {
      taskType: "Click Task",
      pointsRange: "10-50 points",
      description: "Click a link in your email",
      avgTime: "5 seconds"
    },
    {
      taskType: "Email Reply",
      pointsRange: "25-100 points",
      description: "Reply to an email with specific content",
      avgTime: "1-2 minutes"
    },
    {
      taskType: "Survey Task",
      pointsRange: "50-200 points",
      description: "Complete a short survey",
      avgTime: "3-5 minutes"
    },
    {
      taskType: "Bonus Task",
      pointsRange: "100-500 points",
      description: "Special high-value tasks",
      avgTime: "Varies"
    }
  ];

  const timeline = [
    {
      step: 1,
      title: "Request Withdrawal",
      description: "Go to your dashboard and click 'Withdraw'. Enter the amount and select PayPal.",
      icon: ArrowDownCircle
    },
    {
      step: 2,
      title: "Admin Review",
      description: "Our team verifies your request within 24 hours (usually faster).",
      icon: CheckCircle
    },
    {
      step: 3,
      title: "Payment Sent",
      description: "Once approved, payment is sent to your PayPal account.",
      icon: DollarSign
    },
    {
      step: 4,
      title: "Money Received",
      description: "Funds appear in your PayPal within 1-3 business days.",
      icon: Wallet
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-[#00B894] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
          <Link to="/register">
            <Button className="bg-[#00B894] hover:bg-[#00A085] text-white">
              Start Earning Now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-white via-[#00B894]/5 to-[#0984E3]/5">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#00B894]/10 text-[#00B894] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Coins className="w-4 h-4" />
            Simple & transparent rewards
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Understanding Points &{" "}
            <span className="bg-gradient-to-r from-[#00B894] to-[#0984E3] bg-clip-text text-transparent">
              Payments
            </span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Our reward system is simple: complete tasks, earn points, get paid. 
            Here's everything you need to know about turning your efforts into real money.
          </p>
        </div>
      </section>

      {/* Key Info Banner */}
      <section className="py-8 px-6 bg-[#00B894]">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6 text-center text-white">
            <div>
              <p className="text-4xl font-bold mb-1">100 pts</p>
              <p className="text-white/80">= $1.00 USD</p>
            </div>
            <div>
              <p className="text-4xl font-bold mb-1">$7.00</p>
              <p className="text-white/80">Minimum withdrawal</p>
            </div>
            <div>
              <p className="text-4xl font-bold mb-1">1-3 days</p>
              <p className="text-white/80">Payment processing</p>
            </div>
          </div>
        </div>
      </section>

      {/* Points Conversion Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Points to Cash Conversion
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Converting your points to cash is straightforward. Our fixed rate ensures you always know exactly what you're earning.
              </p>
              <div className="bg-[#00B894]/10 rounded-2xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Calculator className="w-6 h-6 text-[#00B894]" />
                  <h3 className="text-xl font-semibold text-slate-900">Conversion Formula</h3>
                </div>
                <div className="bg-white rounded-xl p-4 text-center">
                  <span className="text-2xl font-bold text-[#00B894]">Points ÷ 100 = USD</span>
                </div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-slate-600">
                  <CheckCircle className="w-5 h-5 text-[#00B894]" />
                  <span>Fixed rate - never fluctuates</span>
                </li>
                <li className="flex items-center gap-3 text-slate-600">
                  <CheckCircle className="w-5 h-5 text-[#00B894]" />
                  <span>No hidden fees or deductions</span>
                </li>
                <li className="flex items-center gap-3 text-slate-600">
                  <CheckCircle className="w-5 h-5 text-[#00B894]" />
                  <span>Points never expire</span>
                </li>
              </ul>
            </div>
            <div className="bg-slate-50 rounded-3xl p-8">
              <h3 className="text-lg font-semibold text-slate-900 mb-6">Quick Reference</h3>
              <div className="space-y-4">
                {conversionRates.map((rate, index) => (
                  <div key={index} className="flex items-center justify-between bg-white rounded-xl px-6 py-4">
                    <div className="flex items-center gap-3">
                      <Coins className="w-5 h-5 text-[#00B894]" />
                      <span className="font-medium text-slate-900">{rate.points.toLocaleString()} points</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                      <span className="font-bold text-[#00B894]">${rate.usd.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Earn Points */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              How to Earn Points
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Different tasks reward different amounts. Here's what you can expect:
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pointsEarning.map((task, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-2xl font-bold text-[#00B894] mb-2">{task.pointsRange}</div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{task.taskType}</h3>
                <p className="text-slate-600 text-sm mb-4">{task.description}</p>
                <div className="flex items-center gap-2 text-slate-500 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>{task.avgTime}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Methods */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Payment Methods
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We offer multiple ways to receive your earnings. Payment method availability varies based on your country.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {paymentMethods.map((method, index) => (
              <div 
                key={index} 
                className={`relative rounded-3xl p-8 ${
                  method.recommended 
                    ? 'bg-gradient-to-br from-[#00B894]/10 to-[#0984E3]/10 border-2 border-[#00B894]' 
                    : 'bg-slate-50 border-2 border-slate-200'
                }`}
              >
                {method.recommended && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00B894] text-white px-4 py-1 rounded-full text-sm font-medium">
                    Recommended
                  </div>
                )}
                <div className="text-center mb-6">
                  <span className="text-4xl">{method.icon}</span>
                  <h3 className="text-2xl font-bold text-slate-900 mt-3">{method.name}</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Minimum</span>
                    <span className="font-semibold text-slate-900">{method.minimumWithdrawal}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Processing</span>
                    <span className="font-semibold text-slate-900">{method.processingTime}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Fees</span>
                    <span className="font-semibold text-[#00B894]">{method.fees}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Availability</span>
                    <span className="font-semibold text-slate-900">{method.availability}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Withdrawal Process Timeline */}
      <section className="py-20 px-6 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">
              Withdrawal Process
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              From request to cash in your account - here's how it works:
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {timeline.map((item, index) => (
              <div key={index} className="text-center relative">
                {index < timeline.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-1/2 w-full h-0.5 bg-[#00B894]/30" />
                )}
                <div className="w-20 h-20 bg-[#00B894]/20 rounded-2xl flex items-center justify-center mx-auto mb-4 relative z-10">
                  <item.icon className="w-10 h-10 text-[#00B894]" />
                </div>
                <div className="text-sm font-medium text-[#00B894] mb-2">Step {item.step}</div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Common Questions
            </h2>
          </div>
          <div className="space-y-6">
            <div className="bg-slate-50 rounded-2xl p-6">
              <h3 className="font-semibold text-slate-900 mb-2">Do points expire?</h3>
              <p className="text-slate-600">No! Your points never expire. Take your time accumulating them and withdraw whenever you're ready.</p>
            </div>
            <div className="bg-slate-50 rounded-2xl p-6">
              <h3 className="font-semibold text-slate-900 mb-2">Is there a maximum withdrawal limit?</h3>
              <p className="text-slate-600">There's no maximum limit per withdrawal. You can withdraw all your available balance at once.</p>
            </div>
            <div className="bg-slate-50 rounded-2xl p-6">
              <h3 className="font-semibold text-slate-900 mb-2">What if my PayPal email is different from my GigsEasy email?</h3>
              <p className="text-slate-600">No problem! You can set any PayPal email in your profile settings. Just make sure it's linked to a valid PayPal account.</p>
            </div>
            <div className="bg-slate-50 rounded-2xl p-6">
              <h3 className="font-semibold text-slate-900 mb-2">Why was my withdrawal delayed?</h3>
              <p className="text-slate-600">Occasional delays can happen due to verification requirements or PayPal processing times. If it's been more than 5 business days, contact our support team.</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-800 mb-2">Important Tax Notice</h3>
                  <p className="text-amber-700 text-sm">Earnings from GigsEasy may be taxable income in your country. You are responsible for reporting earnings and paying applicable taxes. Consult a tax professional for guidance.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#00B894]/10 to-[#0984E3]/10">
        <div className="max-w-4xl mx-auto text-center">
          <Gift className="w-16 h-16 text-[#00B894] mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Ready to Start Earning?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Join GigsEasy today and start converting your time into real money. 
            It's free to sign up!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-[#00B894] hover:bg-[#00A085] text-white px-8">
                Create Free Account
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/faq">
              <Button size="lg" variant="outline" className="border-slate-300">
                More Questions? See FAQ
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
